import React, { useEffect, useMemo, useState } from "react";
import Nom035BarChart from "../results/Nom035BarChart";
import DownloadChartButton from "../DownloadChartButton/DownloadChartButton";
import ghqAnalyticsApi from "../../api/ghqAnalyticsApi";
import "./CgsSummaryChart.css";

function normalizeResponseToChartData(dataResponse = {}) {
  const safeResponse = dataResponse && typeof dataResponse === "object" ? dataResponse : {};
  const rows = Array.isArray(safeResponse.response) ? safeResponse.response : [];

  return rows
    .map((entry) => {
      const key = Object.keys(entry || {})[0];
      const value = key ? entry[key] : null;
      if (!value) return null;

      const orderMatch = key.match(/\d+/);
      const order = orderMatch ? Number(orderMatch[0]) : 0;

      return {
        order,
        name: value.label || `Nivel ${order + 1}`,
        value: Number(value.total) || 0
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.order - b.order)
    .map(({ name, value }) => ({ name, value }));
}

export default function CgsSummaryChart({
  title,
  companyId,
  metricKey,
  isGlobal = false,
  filterConfigs = [],
  rangeTitle = "Rango de puntajes:",
  initialData = null,
  colors = []
}) {
  const [filters, setFilters] = useState(
    filterConfigs.reduce((acc, { name }) => ({ ...acc, [name]: "" }), {})
  );
  const [chartResponse, setChartResponse] = useState(initialData);
  const [loading, setLoading] = useState(false);

  const data = useMemo(() => normalizeResponseToChartData(chartResponse), [chartResponse]);
  const palette =
    colors.length > 0
      ? colors
      : ["var(--color-delilife)", "var(--color-amarillo)", "var(--color-rojo)", "#FFA500"];

  const totalRespondents = chartResponse?.totalRespondents ?? 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!metricKey) return;
    if (!isGlobal && !companyId) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const response = isGlobal
          ? await ghqAnalyticsApi.getSummaryGlobalByMetric(metricKey, filters)
          : await ghqAnalyticsApi.getSummaryByCompanyAndMetric(companyId, metricKey, filters);
        setChartResponse(response);
      } catch (error) {
        setChartResponse(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [companyId, metricKey, isGlobal, JSON.stringify(filters)]);

  return (
    <div className="cgs-summary-chart">
      <div className="cgs-chart-title-row">
        <h3 className="chart-title">{title}</h3>
        {data.length > 0 && <DownloadChartButton title={title} targetSelector=".cgs-summary-chart" />}
      </div>

      <div className="filters">
        {filterConfigs.map(({ name, label, options }) => (
          <div className="filter-item" key={name}>
            <label htmlFor={`${title}-${name}`}>{label}</label>
            <select
              id={`${title}-${name}`}
              name={name}
              value={filters[name]}
              onChange={handleChange}
            >
              <option value="">Todos</option>
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <p className="respondents-label">
        Total encuestados: <span>{totalRespondents}</span>
      </p>

      <div className="chart-and-legend">
        <div className="chart-wrapper">
          {loading ? (
            <p>Cargando datos…</p>
          ) : data.length > 0 ? (
            <Nom035BarChart data={data} colors={palette} twoLineLabels />
          ) : (
            <p>No hay datos para mostrar.</p>
          )}
        </div>

        <div className="legend-section">
          <p className="legend-title">{rangeTitle}</p>
          <ul className="risk-legend">
            {data.map((item, index) => (
              <li key={`${title}-${item.name}`}>
                <span
                  className="legend-bullet"
                  style={{ backgroundColor: palette[index % palette.length] }}
                />
                {item.name}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
